import React, { useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Search as SearchIcon, Close as CloseIcon, ForumOutlined as ChatIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";

import CustomAvatar from "components/common/Avatar";
import CustomChip from "components/common/Chip";
import Text from "components/common/Text";

import { chatSetActiveConversation, chatGetMessages, chatMessageRead } from "store/actions/Chat";
import { joinConversation, emitMessageRead } from "services/chat/socketService";
import { markAsReadApi } from "services/chat/chatApi";

import { getInitials, formatChatTime } from "helpers/index";

function ConversationList() {
  const dispatch = useDispatch();
  const { contacts, conversations, activeConversation, onlineUsers } = useSelector((state) => state.Chat);
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
  const { userId, type: userType } = loggedInUser;
  const [searchQuery, setSearchQuery] = useState("");
  const [showContacts, setShowContacts] = useState(false);

  const getOtherParticipant = useCallback(
    (conv) => {
      const participants = conv.Participants || [];
      return participants.find((p) => !(p.userId === userId && p.userType === userType));
    },
    [userId, userType]
  );

  const filteredConversations = useMemo(() => {
    if (!searchQuery) return conversations;
    const query = searchQuery.toLowerCase();
    return conversations.filter((conv) => {
      const other = getOtherParticipant(conv);
      if (!other?.userInfo) return false;
      const name = `${other.userInfo.firstName} ${other.userInfo.lastName}`.toLowerCase();
      return name.includes(query);
    });
  }, [conversations, searchQuery, getOtherParticipant]);

  const filteredContacts = useMemo(() => {
    if (!searchQuery) return contacts;
    const query = searchQuery.toLowerCase();
    return contacts.filter((c) => {
      const name = `${c.firstName} ${c.lastName}`.toLowerCase();
      return name.includes(query);
    });
  }, [contacts, searchQuery]);

  const handleSelectConversation = (conv) => {
    const { conversationId } = conv;
    dispatch(chatSetActiveConversation(conv));
    dispatch(chatGetMessages({ conversationId }));
    joinConversation(conversationId);
    emitMessageRead(conversationId);
    dispatch(chatMessageRead({ conversationId, currentUserId: userId }));
    markAsReadApi(conversationId);
    setShowContacts(false);
  };

  const handleSelectContact = (contact) => {
    const { userId: contactUserId, userType: contactUserType } = contact;

    const existingConv = conversations.find((conv) => {
      const other = (conv.Participants || []).find((p) => p.userId === contactUserId && p.userType === contactUserType);
      return !!other;
    });

    if (existingConv) {
      handleSelectConversation(existingConv);
    } else {
      dispatch(
        chatSetActiveConversation({
          pending: true,
          targetUserId: contactUserId,
          targetUserType: contactUserType,
          conversationId: null,
          Participants: [
            {
              userId: contactUserId,
              userType: contactUserType,
              userInfo: {
                firstName: contact.firstName,
                lastName: contact.lastName,
                email: contact.email,
                picture: contact.picture,
                pictureVersion: contact.pictureVersion,
              },
              isOnline: contact.isOnline,
              lastSeen: contact.lastSeen,
            },
          ],
        })
      );
    }
    setShowContacts(false);
    setSearchQuery("");
  };

  const isOnline = (uid, uType) => {
    return onlineUsers[`${uid}_${uType}`] || false;
  };

  const handleClearPending = (e) => {
    e.stopPropagation();
    dispatch(chatSetActiveConversation(null));
  };

  const hasConversations = conversations.length > 0;

  return (
    <div className="conversation-list">
      <div className="conversation-list__header">
        <h2>Messages</h2>
        <CustomChip
          label={showContacts ? "Chats" : "New Chat"}
          size="small"
          color="primary"
          onClick={() => setShowContacts(!showContacts)}
        />
      </div>

      {(showContacts || hasConversations) && (
        <div className="conversation-list__search">
          <Text
            name="chatSearch"
            size="small"
            variant="filled"
            placeholder={showContacts ? "Search contacts..." : "Search conversations..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            hideHelperText
            startIcon={<SearchIcon sx={{ color: "var(--color-brand-primary-grey-500)", fontSize: 20 }} />}
          />
        </div>
      )}

      <div className="conversation-list__items">
        {showContacts &&
          filteredContacts.map((contact) => (
            <div
              key={`${contact.userId}_${contact.userType}`}
              className="contact-item"
              onClick={() => handleSelectContact(contact)}
            >
              <div style={{ position: "relative" }}>
                <CustomAvatar size="lg" backgroundColor="var(--color-brand-primary-purple-600)">
                  {getInitials(contact.firstName, contact.lastName)}
                </CustomAvatar>
                {isOnline(contact.userId, contact.userType) && <span className="online-indicator" />}
              </div>
              <div className="contact-item__info">
                <span className="contact-item__name">
                  {contact.firstName} {contact.lastName}
                </span>
                <span className="contact-item__role">{contact.role}</span>
              </div>
            </div>
          ))}

        {!showContacts &&
          hasConversations &&
          filteredConversations.map((conv) => {
            const other = getOtherParticipant(conv);
            const info = other?.userInfo;
            const isActive = activeConversation?.conversationId === conv.conversationId;
            const isPending = activeConversation?.pending && !conv.conversationId && isActive;

            return (
              <div
                key={conv.conversationId || `pending_${other?.userId}`}
                className={`conversation-item ${isActive ? "conversation-item--active" : ""}`}
                onClick={() => handleSelectConversation(conv)}
              >
                <div className="conversation-item__avatar">
                  <CustomAvatar size="lg" backgroundColor="var(--color-brand-primary-purple-600)">
                    {info ? getInitials(info.firstName, info.lastName) : "?"}
                  </CustomAvatar>
                  {other && isOnline(other.userId, other.userType) && <span className="online-indicator" />}
                </div>
                <div className="conversation-item__info">
                  <span className="conversation-item__name">
                    {info ? `${info.firstName} ${info.lastName}` : "Unknown"}
                  </span>
                  <span className="conversation-item__last-message">
                    {conv.lastMessage?.isDeleted ? "Message deleted" : conv.lastMessage?.content || "No messages yet"}
                  </span>
                </div>
                <div className="conversation-item__meta">
                  {isPending ? (
                    <IconButton size="small" onClick={handleClearPending}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  ) : (
                    <>
                      <span className="conversation-item__time">
                        {formatChatTime(conv.lastMessage?.createdAt || conv.updatedAt)}
                      </span>
                      {conv.unreadCount > 0 && <span className="conversation-item__unread">{conv.unreadCount}</span>}
                    </>
                  )}
                </div>
              </div>
            );
          })}

        {!showContacts && !hasConversations && (
          <div className="conversation-list__empty">
            <ChatIcon sx={{ fontSize: 40, color: "var(--color-brand-primary-grey-300)", marginBottom: "8px" }} />
            <span className="conversation-list__empty-title">No conversations yet</span>
            <span className="conversation-list__empty-subtitle">
              Click on <strong>New Chat</strong> to start a conversation
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConversationList;
